import {
  MEETING_TEMPLATE_COLORS,
  type MeetingTemplateKind,
  type MeetingTemplateScope,
} from '@wundo/shared/meetingTemplate.ts';
import { Input } from '../input/input.tsx';
import { Select } from '../select/select.tsx';
import { SettingsRow, SettingsValue } from '../settings/settings-row.tsx';
import { SettingsSection } from '../settings/settings-section.tsx';
import { Textarea } from '../textarea/textarea.tsx';
import { TypeColorPicker } from '../type-pill/type-color-picker.tsx';
import type { MeetingTemplateEditorLabels } from './meeting-template-editor-labels.ts';
import type {
  MeetingTemplateCohortOption,
  MeetingTemplateTrackOption,
} from './meeting-template-types.ts';
import { TemplateAudienceRow } from './template-audience-row.tsx';
import { templateColorOf, templateColorVariant } from './template-color-variant.ts';
import { offersAudience, type TemplateDraftState } from './template-draft.ts';

const MEETING_TEMPLATE_KINDS: MeetingTemplateKind[] = ['one_on_one', 'team', 'other'];
const MEETING_TEMPLATE_SCOPES: MeetingTemplateScope[] = ['organisation', 'personal'];

interface TemplateDetailsProps {
  draft: TemplateDraftState;
  onChange: (patch: Partial<TemplateDraftState>) => void;
  tracks: MeetingTemplateTrackOption[];
  cohorts: MeetingTemplateCohortOption[];
  wholeOrganisationOnly: boolean;
  labels: MeetingTemplateEditorLabels;
  kindEditable: boolean;
}

/** Everything about a template except its topics: name, colour, kind, who it is for. */
export function TemplateDetails({
  draft,
  onChange,
  tracks,
  cohorts,
  wholeOrganisationOnly,
  labels,
  kindEditable,
}: TemplateDetailsProps) {
  const { kind, scope } = draft;

  return (
    <SettingsSection title={labels.title}>
      <SettingsRow label={labels.nameLabel}>
        <Input
          aria-label={labels.nameLabel}
          value={draft.name}
          placeholder={labels.namePlaceholder}
          onChange={(event) => onChange({ name: event.target.value })}
        />
      </SettingsRow>
      <SettingsRow label={labels.descriptionLabel} description={labels.descriptionHint}>
        <Textarea
          aria-label={labels.descriptionLabel}
          value={draft.description}
          placeholder={labels.descriptionPlaceholder}
          onChange={(event) => onChange({ description: event.target.value })}
        />
      </SettingsRow>
      <SettingsRow label={labels.colorLabel} description={labels.colorHint}>
        <TypeColorPicker
          aria-label={labels.colorLabel}
          options={MEETING_TEMPLATE_COLORS.map(templateColorVariant)}
          value={templateColorVariant(draft.color)}
          labelOf={(variant) => labels.colors[templateColorOf(variant)]}
          onValueChange={(variant) => onChange({ color: templateColorOf(variant) })}
        />
      </SettingsRow>
      {kind && (
        <SettingsRow label={labels.kindLabel} description={labels.kindHint}>
          {kindEditable ? (
            <Select
              aria-label={labels.kindLabel}
              value={kind}
              options={MEETING_TEMPLATE_KINDS.map((value) => ({
                value,
                label: labels.kinds[value],
              }))}
              onChange={(event) =>
                onChange({ kind: event.target.value as MeetingTemplateKind })
              }
            />
          ) : (
            <SettingsValue>{labels.kinds[kind]}</SettingsValue>
          )}
        </SettingsRow>
      )}
      {scope && (
        <SettingsRow label={labels.scopeLabel} description={labels.scopeHint}>
          <Select
            aria-label={labels.scopeLabel}
            value={scope}
            options={MEETING_TEMPLATE_SCOPES.map((value) => ({
              value,
              label: labels.scopes[value],
            }))}
            onChange={(event) =>
              onChange({ scope: event.target.value as MeetingTemplateScope })
            }
          />
        </SettingsRow>
      )}
      {offersAudience(draft, tracks, cohorts) && (
        <TemplateAudienceRow
          value={draft.audience}
          tracks={tracks}
          cohorts={cohorts}
          wholeOrganisationOnly={wholeOrganisationOnly}
          labels={labels}
          onChange={(audience) => onChange({ audience })}
        />
      )}
    </SettingsSection>
  );
}
